import { supabase } from '@/lib/supabase';
import { cartService } from './cart';

export interface CheckoutItem {
  productId: string;
  productName: string;
  quantity: number;
}

export interface ValidationResult {
  valid: boolean;
  message?: string;
  token?: string;
  insufficientStock?: Array<{
    productId: string;
    productName: string;
    requestedQuantity: number;
    availableStock: number;
  }>;
}

class CheckoutService {
  /**
   * 결제 전 최종 재고 검증
   */
  async validateStock(cartId: string): Promise<ValidationResult> {
    try {
      const validationResult = await cartService.validateCartStock(cartId);
      
      if (validationResult.success) {
        // Generate checkout token
        const token = `checkout_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        return { valid: true, token };
      }
      
      return {
        valid: false,
        message: `다음 상품의 재고가 부족합니다: ${validationResult.unavailableItems.join(', ')}`,
        insufficientStock: validationResult.unavailableItems.map(name => ({
          productId: '',
          productName: name,
          requestedQuantity: 0,
          availableStock: 0
        }))
      };
    } catch (error) {
      console.error('Stock validation error:', error);
      return {
        valid: false,
        message: '재고 확인 중 오류가 발생했습니다.'
      };
    }
  }

  /**
   * 결제 완료 처리 - 실제 재고 소모
   * @param cartId 장바구니 ID
   * @param orderType 주문 유형 (예약할인인 경우 재고 차감하지 않음)
   */
  async completeCheckout(cartId: string, orderType?: string): Promise<{ success: boolean; error?: string }> {
    try {
      // 최종 재고 검증
      const validation = await this.validateStock(cartId);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.message || '재고가 부족합니다.'
        };
      }

      // 마감할인인 경우 추가 offer 유효성 검증
      if (orderType === 'DEADLINE_DISCOUNT') {
        const offerValidation = await this.validateDeadlineDiscountOffers(cartId);
        if (!offerValidation.valid) {
          return {
            success: false,
            error: offerValidation.message || '마감할인 상품이 유효하지 않습니다.'
          };
        }
      }

      // 재고 소모 (예약할인은 제외)
      const consumeResult = await cartService.consumeCartStock(cartId, orderType);
      if (!consumeResult.success) {
        return {
          success: false,
          error: consumeResult.error || '재고 차감 중 오류가 발생했습니다.'
        };
      }

      return { success: true };
    } catch (error) {
      console.error('Complete checkout error:', error);
      return {
        success: false,
        error: '결제 처리 중 오류가 발생했습니다.'
      };
    }
  }

  /**
   * 실시간 재고 확인 (결제 페이지에서 사용)
   */
  async checkRealTimeStock(productIds: string[]): Promise<{
    success: boolean;
    data?: Array<{
      id: string;
      name: string;
      current_stock: number;
      is_available: boolean;
    }>;
    error?: string;
  }> {
    try {
      const today = new Date().toISOString().split('T')[0];
      const stockResults = [];
      
      for (const productId of productIds) {
        // Get current stock using RPC function
        const { data: stockData } = await supabase.rpc('get_product_stock_with_fallback', {
          p_product_id: productId,
          p_date: today
        });

        // Get product info
        const { data: productData } = await supabase
          .from('products')
          .select('id, name, is_available')
          .eq('id', productId)
          .single();

        if (stockData && stockData.length > 0 && productData) {
          stockResults.push({
            id: productData.id,
            name: productData.name,
            current_stock: stockData[0].current_stock,
            is_available: productData.is_available && stockData[0].current_stock > 0
          });
        }
      }

      return { success: true, data: stockResults };
    } catch (error) {
      console.error('Real-time stock check service error:', error);
      return { success: false, error: '실시간 재고 확인 중 오류가 발생했습니다.' };
    }
  }

  /**
   * 장바구니 기반 재고 확인 (간편한 인터페이스)
   */
  async validateCartStock(cartId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const validation = await this.validateStock(cartId);
      return {
        success: validation.valid,
        message: validation.message
      };
    } catch (error) {
      console.error('Cart stock validation error:', error);
      return {
        success: false,
        message: '재고 확인 중 오류가 발생했습니다.'
      };
    }
  }

  /**
   * 마감할인 offer 유효성 검증
   * @param cartId 장바구니 ID
   */
  private async validateDeadlineDiscountOffers(cartId: string): Promise<ValidationResult> {
    try {
      const today = new Date().toISOString().split('T')[0];
      const now = new Date();

      // 장바구니 아이템들의 offer 정보 조회
      const { data: cartItems, error } = await supabase
        .from('cart_items')
        .select(`
          *,
          product_offers(
            id,
            offer_type,
            is_active,
            valid_from,
            valid_until
          )
        `)
        .eq('cart_id', cartId);

      if (error || !cartItems) {
        return {
          valid: false,
          message: '장바구니 정보를 확인할 수 없습니다.'
        };
      }

      // 마감할인 offer들 검증
      const invalidOffers = [];
      for (const item of cartItems) {
        const offer = item.product_offers;
        if (!offer || offer.offer_type !== 'DEADLINE_DISCOUNT') continue;

        // 기본 활성화 검증
        if (!offer.is_active) {
          invalidOffers.push(`상품 ID ${item.product_id}: offer가 비활성화됨`);
          continue;
        }

        // 당일 등록 검증
        if (offer.valid_from) {
          const validFromDate = new Date(offer.valid_from).toISOString().split('T')[0];
          if (validFromDate !== today) {
            invalidOffers.push(`상품 ID ${item.product_id}: 당일 등록되지 않은 offer`);
            continue;
          }
        }

        // 만료 검증
        if (offer.valid_until) {
          const validUntil = new Date(offer.valid_until);
          if (now > validUntil) {
            invalidOffers.push(`상품 ID ${item.product_id}: 만료된 offer`);
            continue;
          }
        }
      }

      if (invalidOffers.length > 0) {
        return {
          valid: false,
          message: `마감할인 상품이 유효하지 않습니다: ${invalidOffers.join(', ')}`
        };
      }

      return { valid: true };
    } catch (error) {
      console.error('Deadline discount offer validation error:', error);
      return {
        valid: false,
        message: '마감할인 상품 검증 중 오류가 발생했습니다.'
      };
    }
  }

}

export const checkoutService = new CheckoutService();